import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './Participant.css';
import ScreeningInterview from './ScreeningInterview';

const InviteLanding = () => {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const [inviteData, setInviteData] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [started, setStarted] = useState(false);
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    loadInviteData();
  }, [inviteToken]);

  const loadInviteData = async () => {
    try {
      const response = await fetch(`/api/participant/invite/${inviteToken}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setInviteData(data);
        setBranding(data.branding);
        
        // Apply all branding CSS variables
        if (data.branding) {
          document.documentElement.style.setProperty('--primary-color', data.branding.primary_color || '#3B82F6');
          document.documentElement.style.setProperty('--secondary-color', data.branding.secondary_color || '#1E40AF');
          document.documentElement.style.setProperty('--page-background', data.branding.page_background || '#f5f7fa');
          document.documentElement.style.setProperty('--panel-background', data.branding.panel_background || '#ffffff');
          document.documentElement.style.setProperty('--header-background', data.branding.header_background || '#ffffff');
          document.documentElement.style.setProperty('--primary-text-color', data.branding.primary_text_color || '#1a1a1a');
          document.documentElement.style.setProperty('--secondary-text-color', data.branding.secondary_text_color || '#666666');
          document.documentElement.style.setProperty('--heading-color', data.branding.heading_color || '#1a1a1a');
          document.documentElement.style.setProperty('--button-background', data.branding.button_background || '#3B82F6');
          document.documentElement.style.setProperty('--button-text-color', data.branding.button_text_color || '#ffffff');
          document.documentElement.style.setProperty('--button-border-radius', data.branding.button_border_radius || '6px');
          document.documentElement.style.setProperty('--button-hover-opacity', data.branding.button_hover_opacity || '0.9');
          document.documentElement.style.setProperty('--ai-bubble-background', data.branding.ai_bubble_background || '#eef6ff');
          document.documentElement.style.setProperty('--ai-bubble-text-color', data.branding.ai_bubble_text_color || '#062b4a');
          document.documentElement.style.setProperty('--user-bubble-background', data.branding.user_bubble_background || '#25d366');
          document.documentElement.style.setProperty('--user-bubble-text-color', data.branding.user_bubble_text_color || '#ffffff');
          document.documentElement.style.setProperty('--panel-border-radius', data.branding.panel_border_radius || '8px');
          document.documentElement.style.setProperty('--bubble-border-radius', data.branding.bubble_border_radius || '18px');
          document.documentElement.style.setProperty('--panel-padding', data.branding.panel_padding || '20px');
          document.documentElement.style.setProperty('--bubble-padding', data.branding.bubble_padding || '12px 16px');
          
          if (data.branding.font_family) {
            document.body.style.fontFamily = data.branding.font_family;
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load invite');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartScreening = async () => {
    try {
      const response = await fetch(
        `/api/participant/session/${inviteData.session_id}/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            invite_token: inviteToken,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSessionData(data);
        setStarted(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to start session');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="participant-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="participant-container">
        <div className="participant-card">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (started && sessionData) {
    return (
      <ScreeningInterview
        sessionId={sessionData.session_id}
        testId={sessionData.test_id}
        inviteToken={inviteToken}
        branding={branding}
      />
    );
  }

  return (
    <div className="participant-container">
      <div className="participant-card branded-card">
        {branding?.logo_base64 && (
          <div className="branded-logo">
            <img src={branding.logo_base64} alt="Company Logo" />
          </div>
        )}

        <h1 style={{ color: branding?.primary_color || '#3B82F6' }}>
          Welcome to the Screening
        </h1>

        <div className="invite-details">
          {inviteData?.company_name && (
            <p className="company-name">{inviteData.company_name}</p>
          )}
          {inviteData?.job_title && (
            <p className="job-title">Position: {inviteData.job_title}</p>
          )}
          {inviteData?.persona && (
            <p className="persona">Interviewer: {inviteData.persona}</p>
          )}
        </div>

        <div className="instructions">
          <h3>Instructions</h3>
          <ul>
            <li>This is a real-time voice interview</li>
            <li>Make sure you have a working microphone</li>
            <li>Find a quiet environment</li>
            <li>The interview will take approximately 10-15 minutes</li>
            <li>Answer questions naturally and clearly</li>
          </ul>
        </div>

        <button onClick={handleStartScreening} className="start-btn">
          Start Screening
        </button>
      </div>
    </div>
  );
};

export default InviteLanding;

